import React, { useEffect, useRef, useState } from "react";
import { useActivityNoteStore } from "../../../../config/stores";
import AntdModal from "../../../../components/common/AntdModal";
import ActivityConceptNotesActivitiesTable from "./ActivitiesTable";
import ActivitiesForm from "./ActivitiesForm";
import RecordStatusButtons from "../../../../components/common/FormStatusButtons";
import { Form, Space, Tabs, message } from "antd";
import dayjs from "dayjs";
import AppInput from "../../../../components/common/AppInput";
import { filter, find, isEmpty, map } from "lodash";
import { useSelector } from "react-redux";
import AppButton from "../../../../components/common/AppButton";
import AppLoader from "../../../../components/common/AppLoader";
import {
  AiOutlineDownload,
  AiOutlinePrinter,
  AiOutlineReload,
} from "react-icons/ai";
import PrintConceptNote from "./PrintConceptNote";
import { useReactToPrint } from "react-to-print";
import InvitedParticipantsInput from "./InvitedParticipantsInput";
import {
  conceptNoteCellStyle,
  conceptNoteTableStyle,
  conceptNoteTextFields,
  INVITED_PARTICIPANTS_LABEL,
  subTitleColumnStyle,
  wrapOptionRender,
} from "./conceptNoteFields";
import generateActivityConceptNotePdf from "../../../../config/services/activityConceptNotePdf";

const ConceptNoteDetails = () => {
  const activityConceptNotesStore = useActivityNoteStore();
  const {
    showActivityNoteDetails,
    activityNoteDetails,
    gettingActivityNoteDetails,
    metaData,
  } = activityConceptNotesStore;
  const { approvedBudget } = useSelector((state) => state.budgetState);
  const [thematicAreas, setThematicAreas] = useState([]);
  const [activities, setActivities] = useState([]);
  const [form] = Form.useForm();
  const strategicPlanId = Form.useWatch("strategic_plan_id", form);
  const thematicAreaId = Form.useWatch("thematic_area_id", form);

  useEffect(() => {
    if (isEmpty(metaData)) activityConceptNotesStore.getMetaData();
  }, [metaData]);

  const closeModal = () => {
    activityConceptNotesStore.setShowViewActivityNoteDetails(false);
  };

  useEffect(() => {
    form.setFieldsValue({
      ...activityNoteDetails,
      date: activityNoteDetails?.date
        ? dayjs(activityNoteDetails.date)
        : undefined,
      donor: activityNoteDetails?.project?.donors?.name,
    });
  }, [activityNoteDetails, form]);

  useEffect(() => {
    if (strategicPlanId) {
      const strategicPlan = find(metaData.strategicPlans, {
        id: strategicPlanId,
      });
      setThematicAreas(strategicPlan?.strategicPlanThematicAreas);
    }

    if (strategicPlanId && thematicAreaId) {
      const thematicArea = find(thematicAreas, { id: thematicAreaId });
      setActivities(thematicArea?.strategicPlansProposedActivities);
    }
  }, [strategicPlanId, thematicAreaId, metaData, thematicAreas, form]);

  const [downloading, setDownloading] = useState(false);
  const onDownload = async () => {
    setDownloading(true);
    try {
      await generateActivityConceptNotePdf(activityNoteDetails);
    } catch {
      message.error("Unable to generate the concept note PDF");
    } finally {
      setDownloading(false);
    }
  };

  const contentRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle: "ACTIVITY CONCEPT NOTE",
  });

  const onPrint = () => {
    setTimeout(() => {
      handlePrint();
    }, [10]);
  };

  return (
    <AntdModal
      title={
        gettingActivityNoteDetails ? "Loading..." : activityNoteDetails?.title
      }
      closeModal={closeModal}
      customSize={1000}
      open={showActivityNoteDetails}
      footer={
        <Space>
          <RecordStatusButtons
            requiresSubmitted
            hasRejection
            data={activityNoteDetails}
            storeActions={{
              approve: activityConceptNotesStore.approveActivityNote,
              approving: activityConceptNotesStore.approvingActivityNote,
              reject: activityConceptNotesStore.rejectActivityNote,
              rejecting: activityConceptNotesStore.rejectingActivityNote,
              submit: activityConceptNotesStore.submitActivityNote,
              submitting: activityConceptNotesStore.submittingActivityNote,
              check: activityConceptNotesStore.checkActivityNote,
              checking: activityConceptNotesStore.checkingActivityNote,
            }}
          />
        </Space>
      }
    >
      {gettingActivityNoteDetails ? (
        <AppLoader message="LOADING..." />
      ) : (
        <Tabs
          tabBarExtraContent={
            <Space>
              <AppButton
                iconBefore={<AiOutlinePrinter />}
                loading={gettingActivityNoteDetails}
                text="PRINT"
                type="default"
                onClick={onPrint}
              />
              <AppButton
                iconBefore={<AiOutlineDownload />}
                loading={gettingActivityNoteDetails || downloading}
                loadingText="DOWNLOADING..."
                text="DOWNLOAD PDF"
                type="default"
                onClick={onDownload}
              />
              <AppButton
                iconBefore={<AiOutlineReload />}
                loading={gettingActivityNoteDetails}
                text="RELOAD"
                type="dashed"
                loadingText="RELOADING..."
                onClick={() =>
                  activityConceptNotesStore.getActivityNoteDetails(
                    activityNoteDetails.id
                  )
                }
              />
            </Space>
          }
          items={[
            {
              key: "concept-note",
              label: "Concept Note",
              children: (
                <>
                  <Form layout="horizontal" form={form}>
                    <table
                      className="table table-sm table-bordered align-middle"
                      style={conceptNoteTableStyle}
                    >
                      <thead className="table-light">
                        <tr>
                          <th style={subTitleColumnStyle}>Sub-Titles</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={conceptNoteCellStyle} className="fw-semibold">Project Name</td>
                          <td style={conceptNoteCellStyle}>
                            <AppInput
                              name="project_id"
                              type="select"
                              rules={[
                                {
                                  required: true,
                                  message: "Please select a project",
                                },
                              ]}
                              inputAttributes={{
                                variant: "borderless",
                                disabled:
                                  activityNoteDetails.status === "approved",
                              }}
                              options={[
                                {
                                  disabled: true,
                                  label: activityNoteDetails?.project?.name,
                                  value: activityNoteDetails?.project?.id,
                                },
                              ]}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td style={conceptNoteCellStyle} className="fw-semibold">Funder</td>
                          <td style={conceptNoteCellStyle}>
                            <AppInput
                              name="donor"
                              type="select"
                              inputAttributes={{
                                variant: "borderless",
                                disabled:
                                  activityNoteDetails.status === "approved",
                              }}
                              options={[
                                {
                                  disabled: true,
                                  label:
                                    activityNoteDetails?.project?.donors?.name,
                                  value:
                                    activityNoteDetails?.project?.donors?.name,
                                },
                              ]}
                            />
                          </td>
                        </tr>

                        <tr>
                          <td style={conceptNoteCellStyle} className="fw-semibold">Budget Line</td>
                          <td style={conceptNoteCellStyle}>
                            <AppInput
                              name="budget_line_id"
                              type="select"
                              inputAttributes={{
                                variant: "borderless",
                                disabled:
                                  activityNoteDetails.status === "approved",
                              }}
                              rules={[
                                {
                                  required: true,
                                  message: "This field is required",
                                },
                              ]}
                              options={filter(approvedBudget, {
                                requires_concept_note: true,
                              }).map((budget) => ({
                                label: budget.name,
                                value: budget.id,
                              }))}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td style={conceptNoteCellStyle} className="fw-semibold">Strategic Plan Priority Input</td>
                          <td style={conceptNoteCellStyle}>
                            {" "}
                            <AppInput
                              name="strategic_plan_id"
                              type="select"
                              inputAttributes={{
                                variant: "borderless",
                                disabled:
                                  activityNoteDetails.status === "approved",
                              }}
                              rules={[
                                {
                                  required: true,
                                  message: "This field is required",
                                },
                              ]}
                              options={map(
                                metaData.strategicPlans,
                                (strategicPlan) => ({
                                  label: strategicPlan.title,
                                  value: strategicPlan.id,
                                })
                              )}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td style={conceptNoteCellStyle} className="fw-semibold">Department/Thematic Area</td>
                          <td style={conceptNoteCellStyle}>
                            <AppInput
                              name="thematic_area_id"
                              type="select"
                              inputAttributes={{
                                variant: "borderless",
                                disabled:
                                  activityNoteDetails.status === "approved",
                              }}
                              options={map(thematicAreas, (thematicArea) => ({
                                label: thematicArea.thematic_area,
                                value: thematicArea.id,
                              }))}
                              rules={[
                                {
                                  required: true,
                                  message: "This field is required",
                                },
                              ]}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td style={conceptNoteCellStyle} className="fw-semibold">Activity Name</td>
                          <td style={conceptNoteCellStyle}>
                            <AppInput
                              name="activity_id"
                              type="select"
                              inputAttributes={{
                                variant: "borderless",
                                disabled:
                                  activityNoteDetails.status === "approved",
                                optionRender: wrapOptionRender,
                              }}
                              rules={[
                                {
                                  required: true,
                                  message: "This field is required",
                                },
                              ]}
                              options={map(activities, (activity) => ({
                                label: activity.activity,
                                value: activity.id,
                              }))}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td style={conceptNoteCellStyle} className="fw-semibold">Activity Dates</td>
                          <td style={conceptNoteCellStyle}>
                            <AppInput
                              name="date"
                              type="date"
                              inputAttributes={{
                                variant: "borderless",
                                placeholder: "Enter Date",
                                disabled:
                                  activityNoteDetails.status === "approved",
                              }}
                              rules={[
                                {
                                  required: true,
                                  message: "This field is required",
                                },
                              ]}
                            />
                          </td>
                        </tr>
                        {conceptNoteTextFields.map((field) => (
                          <tr key={field.name}>
                            <td style={conceptNoteCellStyle} className="fw-semibold">{field.label}</td>
                            <td style={conceptNoteCellStyle}>
                              <AppInput
                                name={field.name}
                                type={field.bulleted ? "bullets" : "textarea"}
                                inputAttributes={{
                                  variant: "borderless",
                                  placeholder: `Enter ${field.label}`,
                                  autoSize: { minRows: 2 },
                                  readOnly:
                                    activityNoteDetails.status === "approved",
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={2}>
                            <p className="mb-2 fw-semibold">{INVITED_PARTICIPANTS_LABEL}</p>
                            <InvitedParticipantsInput readOnly />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </Form>
                </>
              ),
            },
            {
              label: "Proposed Detailed Activity Agenda or Program",
              key: "programme",
              children: (
                <>
                  <ActivitiesForm />
                  <ActivityConceptNotesActivitiesTable
                    canEdit
                    activities={activityNoteDetails.activities}
                  />
                </>
              ),
            },
          ]}
        />
      )}
      <div className="d-none">
        <div className="p-2 border m-2" ref={contentRef}>
          <PrintConceptNote />
        </div>
      </div>
    </AntdModal>
  );
};

export default ConceptNoteDetails;
